/**
 * Alterar o KM da O.S. — só admin, sempre com motivo (decisão de 21/10/2026).
 *
 * Mostra o KM atual da O.S. e a última leitura conhecida da placa (telemetria
 * GOBRAX, abastecimento ou checklist) como referência. A alteração vai para o
 * histórico da O.S. com de → para, o motivo e quem alterou.
 */
import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@tanstack/react-query";
import type { MaintenanceWorkOrderDetailDto } from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { fmtNumber } from "../../lib/format";
import { IconX } from "../../components/icons";
import { dicaKm, erroAlteracaoKm, fmtKmInput, parseKm, ODOMETER_REASON_MIN } from "./work-order-odometer-ui";

function quando(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return ` · ${d.toLocaleDateString("pt-BR")} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}

export function WorkOrderOdometerModal({ o, onClose, onSaved }: {
  o: MaintenanceWorkOrderDetailDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const atual = o.odometerKm;
  const ref = o.lastKnownOdometer;
  // Sem KM na O.S., a leitura mais recente da placa é o ponto de partida.
  const [km, setKm] = useState(() => fmtKmInput(String(atual ?? ref?.km ?? "")));
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  const novo = parseKm(km);
  const dica = dicaKm(atual, novo);

  const salvar = useMutation({
    mutationFn: () => api.patch(`/maintenance/work-orders/${o.id}`, { odometerKm: novo, odometerReason: motivo.trim() }),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível alterar o KM."),
  });

  const enviar = () => {
    const e = erroAlteracaoKm(atual, novo, motivo);
    if (e) { setErro(e); return; }
    salvar.mutate();
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-gray-900/40 p-0 sm:p-4" role="dialog" aria-modal="true" aria-label="Alterar quilometragem">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-xl shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-border">
          <h3 className="text-base font-semibold text-gray-900">Alterar quilometragem</h3>
          <button className="btn-ghost p-1" onClick={onClose} aria-label="Fechar"><IconX className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <dl className="rounded-lg bg-surface-muted px-3 py-2 text-sm space-y-1">
            <div className="flex justify-between gap-3">
              <dt className="text-gray-500">KM atual da O.S.</dt>
              <dd className="font-medium text-gray-900">{atual != null ? `${fmtNumber(atual)} km` : "—"}</dd>
            </div>
            {ref && (
              <div className="flex justify-between gap-3">
                <dt className="text-gray-500">Última leitura da placa</dt>
                <dd className="text-right text-gray-700">
                  {fmtNumber(ref.km)} km
                  <span className="block text-xs text-gray-400">{ref.sourceLabel}{quando(ref.readAt)}</span>
                </dd>
              </div>
            )}
          </dl>

          <label className="block">
            <span className="text-sm text-gray-700">Novo KM</span>
            <input
              className="input w-full mt-1 font-mono"
              inputMode="numeric"
              autoFocus
              value={km}
              onChange={(e) => { setKm(fmtKmInput(e.target.value)); setErro(null); }}
            />
            {dica && (
              <span className={`block mt-1 text-xs ${dica.tom === "alerta" ? "text-warning-700" : "text-gray-500"}`}>{dica.texto}</span>
            )}
          </label>

          <label className="block">
            <span className="text-sm text-gray-700">Motivo da alteração <span className="text-danger-600">*</span></span>
            <textarea
              className="input w-full mt-1"
              rows={3}
              maxLength={500}
              placeholder="KM conferido no painel do veículo na entrada da oficina"
              value={motivo}
              onChange={(e) => { setMotivo(e.target.value); setErro(null); }}
            />
            <span className="block mt-1 text-xs text-gray-400">
              Fica no histórico da O.S. com o seu nome. Mínimo de {ODOMETER_REASON_MIN} caracteres.
            </span>
          </label>

          {erro && <p className="text-sm text-danger-600">{erro}</p>}

          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={onClose}>Cancelar</button>
            <button className="btn-primary" disabled={salvar.isPending} onClick={enviar}>
              {salvar.isPending ? "Salvando…" : "Salvar alteração"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
